import { SaveAnalysisButtonProps } from '@modules/analysis/components/SaveAnalysisButton/SaveAnalysisButton';
import { useCreateAnalysis } from '@modules/analysis/hooks/useCreateAnalysis/useCreateAnalysis';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { useGetArtefactScope } from '@modules/common/hooks';
import {
  getAnalysisTypeDisplayName,
  getShortMetadataForDashboard,
} from '@modules/dashboard/helpers';
import {
  DashboardHeader,
  EditableDashboardListItem,
} from '@modules/dashboard/types';
import {
  AnalysisMetadataInput,
  CreateDashboardInput,
  ListDashboardsDocument,
} from 'generated/graphql';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AnalysisV3EventProps,
  createAnalyticsTracker,
} from 'v2/analytics/events';
import { Scope } from 'v2/core/entities/scope';
import { mapToScope } from 'v2/core/repository/edge-server/analysis/toEdgeAnalysisMappers';
import { useGetEQLExpression } from 'v2/core/repository/edge-server/eql/useGetEQLExpression';
import {
  getAnalysisLink,
  getAnalysisRoute,
} from 'v2/core/services/analysis/link';
import useGetAnalysisDeps from 'v2/core/services/analysis/useGetAnalysisDeps';
import useFullscreenLoader from 'v2/core/services/loader/useFullscreenLoader';
import { useDashboardToAddToFromParam } from '../useDashboardToAddToFromParam';

const trackAnalysisEvents = createAnalyticsTracker('analysisV3');

interface SaveAnalysisOptions
  extends Omit<
    SaveAnalysisButtonProps,
    'isDisabled' | 'disabledStateTooltipTitle'
  > {
  dashboardToAddTo?: EditableDashboardListItem;
  shouldRedirect?: boolean;
  eventProps?: Partial<Omit<AnalysisV3EventProps, 'eventName'>>;
  metadataV2?: AnalysisMetadataInput;
}

export const useSaveAnalysis = ({
  eqlProps,
  metadata,
  onAnalysisCreate,
  dashboardToAddTo,
  shouldRedirect = true,
  eventProps,
  useCase,
  metadataV2,
}: SaveAnalysisOptions) => {
  const navigate = useNavigate();
  const {
    dashboardToAddToFromParam,
    rowID: dashboardLayoutRowID,
    cellInsertDirection,
    redirectTo,
    shouldCloneDashboard,
  } = useDashboardToAddToFromParam();

  const dashboard = dashboardToAddTo ?? dashboardToAddToFromParam;

  const { createAnalysis, isLoading, error } = useCreateAnalysis({
    useCase,
    name: '',
    scope: Scope.PRIVATE,
    eqlProps,
    metadata,
    dashboardToAddTo: shouldCloneDashboard ? undefined : dashboard,
    dashboardLayoutRowID,
    cellInsertDirection,
    metadataV2,
    addToDuplicateOfDashboard:
      shouldCloneDashboard && dashboard?.id
        ? {
            dashboardID: dashboard?.id,
            metadataV2: dashboard.metadataV2,
            shouldDuplicateAnalyses: true,
          }
        : undefined,
  });

  const artefactScope = useGetArtefactScope();

  useFullscreenLoader({ isLoading });

  const {
    data: { user, tenantID, tenantSlug },
  } = useGetAnalysisDeps();

  const { eql } = useGetEQLExpression({
    tenantID,
    eqlProps,
  });

  const { showErrorNotification } = useNotificationContext();

  useEffect(() => {
    if (error) {
      showErrorNotification({
        contentComponent: `Failed to create new ${getAnalysisTypeDisplayName(
          useCase,
        )}`,
        props: { isClosable: true },
      });
    }
  }, [error, showErrorNotification, useCase]);

  const onSaveAnalysis = async ({
    name,
    scope,
    newDashboard,
    shouldShowAsFeed,
  }: {
    name: string;
    scope: Scope;
    newDashboard?: DashboardHeader;
    shouldShowAsFeed?: boolean;
  }) => {
    try {
      const shortMetadata = getShortMetadataForDashboard({
        dashboardEmoji: newDashboard?.emoji,
      });
      const addToNewDashboard: CreateDashboardInput | undefined =
        newDashboard?.name
          ? {
              tenantID,
              title: newDashboard.name,
              createdBy: user.id,
              scope: artefactScope,
              metadata: '',
              shortMetadata,
            }
          : undefined;

      const savedAnalysis: any = await createAnalysis(
        {
          variables: {
            tenantID,
            title: name,
            scope: mapToScope(scope),
            panels: [],
            metadata,
            analysisExpresssion: eql ?? '',
            createdBy: user.id ?? '',
            useCase,
            metadataV2,
            addToNewDashboard,
          },
          refetchQueries: addToNewDashboard
            ? [ListDashboardsDocument]
            : undefined,
        },
        {
          shouldShowAsFeed,
        },
      );

      trackAnalysisEvents({
        eventName: 'Analysis Saved',
        name,
        scope,
        link: getAnalysisLink({
          name,
          token: savedAnalysis.data.createAnalysis.viewToken,
          tenantSlug,
          useCase,
        }),
        query: eql,
        metadata,
        useCase,
        ...eventProps,
      });

      if (savedAnalysis?.data?.createAnalysis) {
        const response = savedAnalysis.data.createAnalysis;
        const artefactRoute = getAnalysisRoute({
          name,
          token: response.viewToken,
          useCase,
        });

        onAnalysisCreate?.(
          {
            title: name,
            viewToken: response.viewToken,
            useCase,
          },
          response.createDashboardResponse
            ? {
                title: newDashboard?.name ?? '',
                viewToken: response.createDashboardResponse.viewToken,
              }
            : dashboard,
        );

        if (!dashboardToAddToFromParam && shouldRedirect) {
          navigate(redirectTo || artefactRoute);
        }

        return {
          artefactRoute,
          artefactLink: getAnalysisLink({
            name,
            token: response.viewToken,
            tenantSlug,
            useCase,
          }),
        };
      }

      onAnalysisCreate?.();
    } catch {
      showErrorNotification({
        contentComponent: `Failed to create new ${getAnalysisTypeDisplayName(
          useCase,
        )}.`,
        props: { isClosable: true },
      });
    }

    return {
      artefactRoute: null,
      artefactLink: null,
    };
  };

  return { onSaveAnalysis, isLoading };
};
